import {
  type OrderFailureReason,
  OrderFailureReasonExceedsMaxNegPnlCollat,
  OrderFailureReasonInsufficientBalance,
  OrderFailureReasonInsufficientCollateralIncrease,
  OrderFailureReasonInsufficientCollateralInvert,
  OrderFailureReasonNegativePositionValue,
  OrderFailureReasonNoPositionToClose,
  OrderFailureReasonPerpetualSolvency,
  OrderFailureReasonReferencePriceStale,
  type OrderStatusReason,
  OrderStatusReasonAccountFrozen,
  OrderStatusReasonAmountExceedsAvailableBalance,
  OrderStatusReasonCancelExistingInvalidCloseOrders,
  OrderStatusReasonCantChangeCloseOrder,
  OrderStatusReasonChangeExpiredOrderNeedsNewExpiry,
  OrderStatusReasonClearingExpiredOrder,
  OrderStatusReasonClearingFrozenAccountOrder,
  OrderStatusReasonClearingInvalidCloseOrder,
  OrderStatusReasonClearingRemainingOrderLockBeyondBalance,
  OrderStatusReasonClearingSelfMatchingOrder,
  OrderStatusReasonCloseOrderExceedsPosition,
  OrderStatusReasonCloseOrderPositionMismatch,
  OrderStatusReasonContractNotOperational,
  OrderStatusReasonCrossesBook,
  OrderStatusReasonExceedsLastExecutionBlock,
  OrderStatusReasonForwardingReverted,
  OrderStatusReasonImmediateOrCancelExecuted,
  OrderStatusReasonImmediateOrderUnderMinimum,
  OrderStatusReasonInsuficientFundsForRecycleFee,
  OrderStatusReasonInvalidAccountFrozenOrder,
  OrderStatusReasonInvalidAmount,
  OrderStatusReasonInvalidExpiryBlock,
  OrderStatusReasonInvalidFlags,
  OrderStatusReasonInvalidOrderId,
  OrderStatusReasonInvalidTriggerOrder,
  OrderStatusReasonMakerOrderFilled,
  OrderStatusReasonMakerOrderSettlementFailed,
  OrderStatusReasonMaximumAccountOrders,
  OrderStatusReasonMaxMatchesReached,
  OrderStatusReasonNoOp,
  OrderStatusReasonOrderBookFull,
  OrderStatusReasonOrderCancelled,
  OrderStatusReasonOrderCancelledByAdmin,
  OrderStatusReasonOrderCancelledByLiquidator,
  OrderStatusReasonOrderChanged,
  OrderStatusReasonOrderDescIdTooLow,
  OrderStatusReasonOrderDoesNotExist,
  OrderStatusReasonOrderExtensionRejected,
  OrderStatusReasonOrderForwardingNotAllowed,
  OrderStatusReasonOrderPlaced,
  OrderStatusReasonOrderPostFailed,
  OrderStatusReasonOrderSettlementImpliesInsolvent,
  OrderStatusReasonOrderSizeExceedsAvailableSize,
  OrderStatusReasonPerpetualInsolvent,
  OrderStatusReasonPostOrderUnderMinimum,
  OrderStatusReasonPriceOutOfRange,
  OrderStatusReasonPriceSetDuringTriggerExec,
  OrderStatusReasonRecycleBalanceInsufficientSevere,
  OrderStatusReasonSizeOutOfRange,
  OrderStatusReasonTakerOrderFilled,
  OrderStatusReasonTakerOrderSettlementFailed,
  OrderStatusReasonTriggerDescIdTooLow,
  OrderStatusReasonTriggered,
  OrderStatusReasonTriggeredExecutionAttemptsExhausted,
  OrderStatusReasonTriggeredOrderExecuted,
  OrderStatusReasonTriggeredOrderExpired,
  OrderStatusReasonTriggeredOrderPartiallyFilled,
  OrderStatusReasonTriggeredOrderRecoverableFailure,
  OrderStatusReasonTriggerOrderRequest,
  OrderStatusReasonUnableToCancelOrder,
  OrderStatusReasonUnmatchedLotRemainsInFillOrKill,
  OrderStatusReasonUnspecified,
  OrderStatusReasonUnspecifiedCollateral,
  OrderStatusReasonUnspecifiedPrice,
  OrderStatusReasonUnspecifiedSize,
  OrderStatusReasonValueExceedsMaximum,
  OrderStatusReasonWrongAccountForOrder,
  OrderStatusReasonWrongChainForOrder,
  OrderStatusReasonWrongMarketForOrder,
  OrderStatusReasonWrongTriggerPosition,
} from '@/api/spec';

export interface ErrorInfo {
  title: string;
  message: string;
  severity: 'error' | 'warning' | 'info';
}

export const orderErrorMapping: Record<OrderStatusReason, ErrorInfo> = {
  [OrderStatusReasonUnspecified]: {
    title: 'Unknown Error',
    message:
      'An unspecified error occurred while processing your order. Please try again or contact us on Discord if the issue persists.',
    severity: 'error',
  },

  [OrderStatusReasonAmountExceedsAvailableBalance]: {
    title: 'Insufficient Balance',
    message:
      'The order amount exceeds your available account balance. Please reduce the order size or add funds to your account.',
    severity: 'error',
  },

  [OrderStatusReasonAccountFrozen]: {
    title: 'Account Frozen',
    message: 'Your account is currently frozen and cannot place orders. Please contact us on Discord to unfreeze your account.',
    severity: 'error',
  },

  [OrderStatusReasonCancelExistingInvalidCloseOrders]: {
    title: 'Invalid Close Order Cancellation',
    message: 'Cannot cancel existing invalid close orders. Please review your existing orders.',
    severity: 'warning',
  },

  [OrderStatusReasonCantChangeCloseOrder]: {
    title: 'Cannot Modify Close Order',
    message: 'Close orders cannot be modified. Please cancel the order and submit a new one with the desired parameters.',
    severity: 'error',
  },

  [OrderStatusReasonChangeExpiredOrderNeedsNewExpiry]: {
    title: 'Expiry Required',
    message: 'The order has already expired. Provide a new expiry time and resubmit the order.',
    severity: 'warning',
  },

  [OrderStatusReasonClearingExpiredOrder]: {
    title: 'Expired Order Removed',
    message: 'An expired order has been removed from the order book. No action is required.',
    severity: 'info',
  },

  [OrderStatusReasonClearingFrozenAccountOrder]: {
    title: 'Frozen Account Order Removed',
    message: 'An order linked to a frozen account has been removed. No action is required.',
    severity: 'info',
  },

  [OrderStatusReasonClearingInvalidCloseOrder]: {
    title: 'Invalid Close Order Removed',
    message: 'An invalid close order has been removed from the order book. No action is required.',
    severity: 'info',
  },

  [OrderStatusReasonClearingSelfMatchingOrder]: {
    title: 'Self-Matching Order Cancelled',
    message: 'An order that would have matched with your own position was automatically cancelled. No action is required.',
    severity: 'info',
  },

  [OrderStatusReasonCloseOrderExceedsPosition]: {
    title: 'Order Size Exceeds Position',
    message:
      'The size of your "Reduce-Only" order exceeds your current position size. Please reduce the order size to be less than or equal to your position.',
    severity: 'error',
  },

  [OrderStatusReasonCloseOrderPositionMismatch]: {
    title: 'Incorrect Side for Reduce-Only',
    message:
      'Your "Reduce-Only" order has the wrong side to reduce your current position. For example, to reduce a long position, you must place a sell order.',
    severity: 'error',
  },

  [OrderStatusReasonContractNotOperational]: {
    title: 'Market Paused',
    message: 'The contract for this market is currently paused. Wait for the market to resume or try a different market.',
    severity: 'warning',
  },

  // Raised by the contract's shared posting gate (OrderHandler._postOrderOkay), which every
  // rest-only order passes through — post-only orders AND order changes (OrderChangeHandler
  // ._changeOrder calls the same gate, with no postOnly parameter). So a plain GtC edit that
  // reprices through the spread lands here too; the copy must not blame Post-Only.
  [OrderStatusReasonCrossesBook]: {
    title: 'Order Would Cross the Book',
    message:
      'This price would execute immediately instead of resting on the book, so it was rejected. Orders that can only rest —' +
      ' post-only orders, and any edit to an order already on the book — have to keep their price on the passive side of' +
      ' the spread. Move the price back to the passive side, or trade as a taker with a new order that is not post-only.',
    severity: 'warning',
  },

  [OrderStatusReasonExceedsLastExecutionBlock]: {
    title: 'Invalid Expiry Time',
    message: 'The expiry time is too far in the future. Please select a shorter duration for your order.',
    severity: 'error',
  },

  [OrderStatusReasonForwardingReverted]: {
    title: 'Transaction Reverted',
    message: 'The blockchain transaction was reverted. Please try again or check the blockchain status.',
    severity: 'error',
  },

  [OrderStatusReasonImmediateOrCancelExecuted]: {
    title: 'IOC Order Executed',
    message: 'Your IOC order was executed. Any unfilled portion was cancelled immediately, as expected.',
    severity: 'info',
  },

  [OrderStatusReasonImmediateOrderUnderMinimum]: {
    title: 'Immediate Order Below Minimum',
    message:
      'The immediate order size is below the minimum required amount. Please increase the order size to meet the minimum requirement.',
    severity: 'error',
  },

  [OrderStatusReasonInsuficientFundsForRecycleFee]: {
    title: 'Insufficient Funds for Fee',
    message: 'You have insufficient funds to cover the transaction fee. Add more funds or reduce the order size.',
    severity: 'error',
  },

  [OrderStatusReasonInvalidAccountFrozenOrder]: {
    title: 'Frozen Account Order Invalid',
    message: 'Orders from this frozen account are not allowed. Resolve the account status before placing new orders.',
    severity: 'error',
  },

  [OrderStatusReasonInvalidExpiryBlock]: {
    title: 'Invalid Expiry Time',
    message: 'The expiry time is invalid (e.g., in the past). Please check your time-in-force setting.',
    severity: 'error',
  },

  [OrderStatusReasonInvalidOrderId]: {
    title: 'Invalid Order ID',
    message: 'The supplied order ID is invalid or has expired. Refresh your orders and try again.',
    severity: 'error',
  },

  [OrderStatusReasonMakerOrderFilled]: {
    title: 'Order Filled',
    message: 'Your maker order was filled successfully. View your order history for details.',
    severity: 'info',
  },

  [OrderStatusReasonMakerOrderSettlementFailed]: {
    title: 'Resting Order Not Settled',
    message:
      "A match against your resting order couldn't be settled, so the order was cleared — nothing was charged. This usually" +
      ' means the resulting position would need more margin than your available balance. Add collateral to the position so' +
      ' matches can settle.',
    severity: 'error',
  },

  [OrderStatusReasonMaxMatchesReached]: {
    title: 'Maximum Matches Reached',
    message:
      'The order reached the maximum number of allowed matches. Try placing a smaller order or wait for market conditions to improve.',
    severity: 'warning',
  },

  [OrderStatusReasonMaximumAccountOrders]: {
    title: 'Order Limit Reached',
    message:
      'You have reached the maximum number of open trigger orders allowed for this account. Cancel existing trigger orders before placing new ones.',
    severity: 'warning',
  },

  [OrderStatusReasonNoOp]: {
    title: 'No Changes Applied',
    message: 'The request did not result in any order changes. Verify the order details and try again.',
    severity: 'info',
  },

  [OrderStatusReasonOrderBookFull]: {
    title: 'Order Book Full',
    message:
      'The order book is currently full and cannot accept new orders. Wait a moment and try again, or try a different market.',
    severity: 'warning',
  },

  [OrderStatusReasonOrderCancelled]: {
    title: 'Order Cancelled',
    message: 'Your order was cancelled successfully. No further action required.',
    severity: 'info',
  },

  [OrderStatusReasonOrderCancelledByAdmin]: {
    title: 'Cancelled by Admin',
    message: 'Your order was cancelled by an administrator. Contact us on Discord for more information.',
    severity: 'warning',
  },

  [OrderStatusReasonOrderCancelledByLiquidator]: {
    title: 'Cancelled Due to Liquidation',
    message:
      'Your position was liquidated and this order was cancelled as a result. Visit the Positions tab to review your account health and margin requirements.',
    severity: 'warning',
  },

  [OrderStatusReasonOrderChanged]: {
    title: 'Order Updated',
    message: 'Your order was updated successfully. You can monitor it in the Orders tab.',
    severity: 'info',
  },

  [OrderStatusReasonOrderDescIdTooLow]: {
    title: 'Request Out of Sync',
    message: 'This request arrived out of order. Please refresh and try again.',
    severity: 'info',
  },

  [OrderStatusReasonOrderDoesNotExist]: {
    title: 'Order Not Found',
    message: 'The referenced order could not be found. It may have already been filled or cancelled.',
    severity: 'error',
  },

  [OrderStatusReasonOrderForwardingNotAllowed]: {
    title: 'Forwarding Not Allowed',
    message:
      'Order forwarding is not currently allowed. Check your account settings, contact us on Discord, or try again later.',
    severity: 'error',
  },

  [OrderStatusReasonOrderPlaced]: {
    title: 'Order Placed',
    message: 'Your order was placed successfully. You can track it in the Orders tab.',
    severity: 'info',
  },

  [OrderStatusReasonOrderPostFailed]: {
    title: 'Order Post Failed',
    message:
      'Failed to post the order to the matching engine. Please try again or contact us on Discord if the issue persists.',
    severity: 'error',
  },

  [OrderStatusReasonOrderSettlementImpliesInsolvent]: {
    title: 'Insufficient Collateral for Settlement',
    message:
      'This order cannot be settled as it would result in insufficient collateral in your account. Please add more collateral or reduce the order size.',
    severity: 'error',
  },

  [OrderStatusReasonOrderSizeExceedsAvailableSize]: {
    title: 'Order Size Too Large',
    message:
      'The order size exceeds the available size for this operation. Please reduce the order size or check position limits.',
    severity: 'error',
  },

  [OrderStatusReasonPostOrderUnderMinimum]: {
    title: 'Order Below Minimum',
    message: 'The order size is below the minimum required amount. Increase the order size to meet the minimum requirement.',
    severity: 'error',
  },

  [OrderStatusReasonPriceOutOfRange]: {
    title: 'Price Out of Range',
    message: 'The specified price is outside the allowed trading range. Adjust the price to be within the acceptable range.',
    severity: 'error',
  },

  [OrderStatusReasonRecycleBalanceInsufficientSevere]: {
    title: 'Insufficient Balance for Fee',
    message:
      'There is not enough balance to cover the fee for this order. Reduce the order size or add collateral before retrying.',
    severity: 'error',
  },

  [OrderStatusReasonSizeOutOfRange]: {
    title: 'Size Outside Allowed Range',
    message: 'The order size is outside the allowed range for this market. Adjust the size and try again.',
    severity: 'error',
  },

  [OrderStatusReasonTakerOrderFilled]: {
    title: 'Taker Order Filled',
    message: 'Your taker order was filled successfully. View your order history for details.',
    severity: 'info',
  },

  [OrderStatusReasonTakerOrderSettlementFailed]: {
    title: 'Order Not Completed',
    message:
      "Your order didn't go through and nothing was charged — no funds left your account. This usually happens when opening" +
      ' or growing a position that would need more margin than your available balance allow (common when adding to a' +
      " position that's already at a loss). To proceed, try one of: add collateral to the position, lower your leverage," +
      ' reduce the order size. If you believe this is an error, contact us on Discord.',
    severity: 'error',
  },

  [OrderStatusReasonUnableToCancelOrder]: {
    title: 'Unable to Cancel',
    message: 'The order cannot be cancelled at this time. The order may have already been filled or expired.',
    severity: 'warning',
  },

  [OrderStatusReasonUnmatchedLotRemainsInFillOrKill]: {
    title: 'Fill-or-Kill Not Filled',
    message:
      'The Fill-or-Kill order could not be completely filled and has been cancelled. You can adjust your order parameters or use a different order type.',
    severity: 'error',
  },

  [OrderStatusReasonUnspecifiedCollateral]: {
    title: 'Collateral Not Specified',
    message: 'No collateral asset was specified for this order. Select a collateral asset and resubmit.',
    severity: 'error',
  },

  [OrderStatusReasonUnspecifiedPrice]: {
    title: 'Price Not Specified',
    message: 'The order price was not properly specified. Please enter a valid price for your order.',
    severity: 'error',
  },

  [OrderStatusReasonUnspecifiedSize]: {
    title: 'Size Not Specified',
    message: 'The order size was not properly specified. Please enter a valid size for your order.',
    severity: 'error',
  },

  [OrderStatusReasonWrongAccountForOrder]: {
    title: 'Wrong Account',
    message: 'The order was submitted for the wrong account. Ensure you are using the correct account.',
    severity: 'error',
  },

  [OrderStatusReasonWrongChainForOrder]: {
    title: 'Wrong Network',
    message: 'The order was submitted on the wrong blockchain network. Switch to the correct network and try again.',
    severity: 'error',
  },

  [OrderStatusReasonWrongMarketForOrder]: {
    title: 'Wrong Market',
    message: 'The order was submitted for the wrong market. Select the correct market and try again.',
    severity: 'error',
  },

  [OrderStatusReasonPerpetualInsolvent]: {
    title: 'Market Insolvent',
    message:
      'The market has become insolvent. Trading may be disabled and open orders may be cancelled. Please follow our Discord for more information.',
    severity: 'error',
  },

  [OrderStatusReasonTriggered]: {
    title: 'Order Triggered',
    message: 'Your trigger order was activated successfully.',
    severity: 'info',
  },

  [OrderStatusReasonInvalidAmount]: {
    title: 'Invalid Amount',
    message: 'The specified order amount is invalid. Please check the amount and try again.',
    severity: 'error',
  },

  [OrderStatusReasonInvalidFlags]: {
    title: 'Invalid Flags',
    message: 'The provided order flags are invalid. Please review your order form, or contact us on Discord.',
    severity: 'error',
  },

  [OrderStatusReasonInvalidTriggerOrder]: {
    title: 'Invalid Trigger Order',
    message: 'The options specified for this trigger order are invalid. Please check your trigger settings.',
    severity: 'error',
  },

  [OrderStatusReasonWrongTriggerPosition]: {
    title: 'Invalid Trigger Price',
    message: 'The trigger price is invalid for the current market price (e.g. stop loss below current price for a sell order).',
    severity: 'error',
  },

  [OrderStatusReasonTriggerDescIdTooLow]: {
    title: 'Trigger Request Out of Sync',
    message:
      'The trigger order request is out of sync (client-side request ID was lower than the previously recorded ID). The system will retry automatically.',
    severity: 'info',
  },

  [OrderStatusReasonTriggerOrderRequest]: {
    title: 'Trigger Order Requested',
    message: 'Your trigger order request has been submitted.',
    severity: 'info',
  },

  [OrderStatusReasonValueExceedsMaximum]: {
    title: 'Value Exceeds Maximum',
    message:
      'The order notional value exceeds the maximum allowed by the smart contract limits. Please reduce the order size or leverage.',
    severity: 'error',
  },

  [OrderStatusReasonClearingRemainingOrderLockBeyondBalance]: {
    title: 'Clearing Order Lock Beyond Balance',
    message:
      'The system is clearing an order whose locked amount exceeds your available balance. This is an automatic system process.',
    severity: 'info',
  },

  [OrderStatusReasonPriceSetDuringTriggerExec]: {
    title: 'Price Set During Trigger Execution',
    message: 'A limit price was specified for a trigger order during execution. Please remove the price and resubmit.',
    severity: 'error',
  },

  [OrderStatusReasonTriggeredOrderExecuted]: {
    title: 'Trigger Order Executed',
    message: 'Your trigger order activated and executed successfully. View your order history for details.',
    severity: 'info',
  },

  [OrderStatusReasonTriggeredOrderPartiallyFilled]: {
    title: 'Trigger Order Partially Filled',
    message:
      'Your trigger order activated and was partially filled. The remaining size may keep working or be cancelled depending on the order type — check the Orders tab.',
    severity: 'info',
  },

  [OrderStatusReasonTriggeredExecutionAttemptsExhausted]: {
    title: 'Trigger Order Not Filled',
    message:
      "Your trigger order activated but couldn't be filled after several attempts (e.g. not enough liquidity at its price). The order is no longer active — re-place it if you still want the trade.",
    severity: 'warning',
  },

  [OrderStatusReasonTriggeredOrderExpired]: {
    title: 'Trigger Order Expired',
    message: 'Your trigger order expired before it could fully execute. Re-place it if you still want the trade.',
    severity: 'warning',
  },

  [OrderStatusReasonTriggeredOrderRecoverableFailure]: {
    title: 'Trigger Order Retrying',
    message: 'Your trigger order hit a temporary issue while executing and will be retried automatically. No action is needed.',
    severity: 'info',
  },

  [OrderStatusReasonOrderExtensionRejected]: {
    title: 'Order Extension Rejected',
    message:
      "The order carried an extension that was rejected — for example, a builder fee above the API key's registered maximum. The order was not placed.",
    severity: 'error',
  },
};

/**
 * Copy for `Order.fr`. `sr` says *where* the order died (posting vs. taker / maker settlement), `fr`
 * says *why* — and `fr` is always the more specific of the two, so it drives title, remedy and
 * severity while `sr` only contributes the stage lead below. `Unspecified` and `Other` carry no
 * information and are deliberately absent, falling back to the broader `sr` copy.
 */
const orderFailureMapping: Partial<Record<OrderFailureReason, ErrorInfo>> = {
  [OrderFailureReasonInsufficientBalance]: {
    title: 'Insufficient Balance',
    message:
      'Your account balance is below the margin, negative PnL and fees this new position requires. Add collateral, lower your leverage, or reduce the order size.',
    severity: 'error',
  },

  [OrderFailureReasonInsufficientCollateralIncrease]: {
    title: 'Not Enough Collateral to Increase',
    message:
      "Your available collateral doesn't cover what increasing this position requires — adding to a position that is already at a loss needs extra margin. Add collateral to the position, lower your leverage, or reduce the order size.",
    severity: 'error',
  },

  [OrderFailureReasonInsufficientCollateralInvert]: {
    title: 'Not Enough Collateral to Flip Side',
    message:
      "This order is larger than your open position, so it would flip you to the opposite side, and your available collateral doesn't cover the position that would result. Reduce the order size to at most your position size, or add collateral.",
    severity: 'error',
  },

  [OrderFailureReasonNoPositionToClose]: {
    title: 'No Position to Close',
    message:
      'This close order was evaluated against a position that no longer exists — it may have been closed or liquidated first. Take-profit and stop-loss orders commonly end this way once the position they protected is gone.',
    severity: 'warning',
  },

  [OrderFailureReasonPerpetualSolvency]: {
    title: 'Market Solvency Limit',
    message:
      'Settling this order at its price would leave the market insolvent, so the fill was rejected. Wait for the market to move, or re-place the order at a different price.',
    severity: 'warning',
  },

  [OrderFailureReasonNegativePositionValue]: {
    title: 'Position Value Would Go Negative',
    message:
      'Closing or reducing this position at the current price would realise a negative position value, so the order was rejected. Add collateral to the position before reducing it, or wait for the price to recover.',
    severity: 'error',
  },

  [OrderFailureReasonReferencePriceStale]: {
    title: 'Reference Price Stale',
    message:
      "No fresh reference price was available to value this order against, so it wasn't executed. This is temporary — try again in a moment.",
    severity: 'warning',
  },

  [OrderFailureReasonExceedsMaxNegPnlCollat]: {
    title: 'Negative PnL Limit Exceeded',
    message:
      'Filling this order would have had to collateralize more unrealized negative PnL than this market allows. This usually happens when adding to a position that is already deep in loss — add collateral to the position, or reduce the order size.',
    severity: 'error',
  },
};

/**
 * Prepended to the `fr` cause so the "nothing was charged" reassurance a settlement failure earns
 * doesn't leak onto a posting failure. Without an entry here the `fr` copy stands on its own.
 */
const orderFailureStageLead: Partial<Record<OrderStatusReason, string>> = {
  [OrderStatusReasonTakerOrderSettlementFailed]:
    "Your order didn't go through and nothing was charged — no funds left your account.",
  [OrderStatusReasonMakerOrderSettlementFailed]:
    "A match against your resting order couldn't be settled, so the order was cleared — nothing was charged.",
  [OrderStatusReasonOrderPostFailed]: "Your order couldn't be placed.",
};

/**
 * Maps smart contract ABI error names to OrderStatusReason enum values
 */
export function abiErrorToOrderStatusReason(abiErrorName: string): OrderStatusReason {
  const mapping: Record<string, OrderStatusReason> = {
    // Account-related errors
    AccountFrozen: OrderStatusReasonAccountFrozen,
    AccountDoesNotExist: OrderStatusReasonUnspecified,
    AccountExists: OrderStatusReasonUnspecified,
    AccountIdDoesNotExist: OrderStatusReasonUnspecified,

    // Balance and funding errors
    AmountExceedsAvailableBalance: OrderStatusReasonAmountExceedsAvailableBalance,
    InsufficientFunds: OrderStatusReasonAmountExceedsAvailableBalance,
    ContractInsufficientFunds: OrderStatusReasonAmountExceedsAvailableBalance,
    InsuficientFundsForRecycleFee: OrderStatusReasonInsuficientFundsForRecycleFee,

    // Order cancellation and modification errors
    CancelExistingInvalidCloseOrders: OrderStatusReasonCancelExistingInvalidCloseOrders,
    CantChangeCloseOrder: OrderStatusReasonCantChangeCloseOrder,
    CannotChangeCloseOrderLocks: OrderStatusReasonCantChangeCloseOrder,
    ChangeExpiredOrderNeedsNewExpiry: OrderStatusReasonChangeExpiredOrderNeedsNewExpiry,

    // Order clearing errors
    ClearingExpiredOrder: OrderStatusReasonClearingExpiredOrder,
    ClearingFrozenAccountOrder: OrderStatusReasonClearingFrozenAccountOrder,
    ClearingInvalidCloseOrder: OrderStatusReasonClearingInvalidCloseOrder,
    ClearingSelfMatchingOrder: OrderStatusReasonClearingSelfMatchingOrder,

    // Position and close order errors
    CloseOrderExceedsPosition: OrderStatusReasonCloseOrderExceedsPosition,
    CloseOrderPositionMismatch: OrderStatusReasonCloseOrderPositionMismatch,

    // Contract state errors
    ContractIsPaused: OrderStatusReasonContractNotOperational,
    ExchangeHalted: OrderStatusReasonContractNotOperational,

    // Order book and matching errors
    CrossesBook: OrderStatusReasonCrossesBook,
    OrderBookFull: OrderStatusReasonOrderBookFull,
    OrderBookPriceOutOfRange: OrderStatusReasonPriceOutOfRange,

    // Order validation errors
    ExceedsLastExecutionBlock: OrderStatusReasonExceedsLastExecutionBlock,
    ImmediateOrderUnderMinimum: OrderStatusReasonImmediateOrderUnderMinimum,
    PostOrderUnderMinimum: OrderStatusReasonPostOrderUnderMinimum,
    OrderSizeExceedsAvailableSize: OrderStatusReasonOrderSizeExceedsAvailableSize,
    PriceOutOfRange: OrderStatusReasonPriceOutOfRange,
    LotOutOfRange: OrderStatusReasonSizeOutOfRange,
    ValueOutsideRange: OrderStatusReasonSizeOutOfRange,

    // Order existence and ID errors
    OrderDoesNotExist: OrderStatusReasonOrderDoesNotExist,
    InvalidOrderId: OrderStatusReasonInvalidOrderId,
    CantPostOrder: OrderStatusReasonOrderPostFailed,
    OrderPostFailed: OrderStatusReasonOrderPostFailed,

    // Order limits and constraints
    MaximumAccountOrders: OrderStatusReasonMaximumAccountOrders,
    OrderForwardingNotAllowed: OrderStatusReasonOrderForwardingNotAllowed,

    // Settlement and execution errors
    TakerOrderSettlementFailed: OrderStatusReasonTakerOrderSettlementFailed,
    UnmatchedLotRemainsInFillOrKill: OrderStatusReasonUnmatchedLotRemainsInFillOrKill,
    ImmediateOrCancelExecuted: OrderStatusReasonImmediateOrCancelExecuted,

    // Account and order validation
    WrongAccountForOrder: OrderStatusReasonWrongAccountForOrder,
    InvalidAccountFrozenOrder: OrderStatusReasonInvalidAccountFrozenOrder,
    InvalidExpiryBlock: OrderStatusReasonInvalidExpiryBlock,

    // Position and liquidation errors
    CantLiquidateInsolventPos: OrderStatusReasonOrderSettlementImpliesInsolvent,
    CantLiquidatePosAboveMMR: OrderStatusReasonOrderSettlementImpliesInsolvent,
    CantLiquidatePosOnBook: OrderStatusReasonOrderSettlementImpliesInsolvent,
    CriticalPerpetualInsolvent: OrderStatusReasonOrderSettlementImpliesInsolvent,

    // Contract and system errors
    ContractDoesNotExist: OrderStatusReasonWrongMarketForOrder,
    ContractCannotBeRemoved: OrderStatusReasonUnspecified,
    ContractDecimalsExceedResolution: OrderStatusReasonUnspecified,
    ContractIdExceedsMaximum: OrderStatusReasonUnspecified,
    ContractIdInUse: OrderStatusReasonUnspecified,
    ContractNotPaused: OrderStatusReasonUnspecified,

    // Oracle and price errors
    IndexExceedsOracleTol: OrderStatusReasonPriceOutOfRange,
    IndexPriceUnitialized: OrderStatusReasonPriceOutOfRange,
    OracleAgeExceedsMax: OrderStatusReasonPriceOutOfRange,
    InvalidBankruptcyPrice: OrderStatusReasonPriceOutOfRange,
    InvalidLiquidationPrice: OrderStatusReasonPriceOutOfRange,

    // Funding errors
    FundingEventSetTooEarly: OrderStatusReasonUnspecified,
    FundingExitBlockPreceedsEntryBlock: OrderStatusReasonUnspecified,
    FundingIntervalIncorrect: OrderStatusReasonUnspecified,
    FundingSumAlreadySet: OrderStatusReasonUnspecified,

    // Permission and access errors
    SenderIsNotAdministrator: OrderStatusReasonUnspecified,
    SenderIsNotPositionAdministrator: OrderStatusReasonUnspecified,
    SenderIsNotPriceAdministrator: OrderStatusReasonUnspecified,

    // System and validation errors
    DecrementUnderflows: OrderStatusReasonUnspecified,
    DifferenceExceedsInt256: OrderStatusReasonUnspecified,
    DifferenceExceedsMaximum: OrderStatusReasonUnspecified,
    SumExceedsMaximum: OrderStatusReasonUnspecified,
    ValueExceedsMaximum: OrderStatusReasonValueExceedsMaximum,
    InitialMarginFractionExceedsMaintenance: OrderStatusReasonUnspecified,
    InvalidProposedInitFraction: OrderStatusReasonUnspecified,
    InvalidProposedMaintFraction: OrderStatusReasonUnspecified,
    InvalidWithdrawLimitThousandths: OrderStatusReasonUnspecified,
    LiquidationParamsExceedUnity: OrderStatusReasonUnspecified,
    UnityMustBeLessThanOverColDescent: OrderStatusReasonUnspecified,
    ProposedFeeExceedsMax: OrderStatusReasonUnspecified,
    ProposedFundingRateClampExceedsMax: OrderStatusReasonUnspecified,
    ProposedIndexOracleAgeExceedsMax: OrderStatusReasonUnspecified,
    ProposedIndexOracleTolExceedsMax: OrderStatusReasonUnspecified,
    ReferencePriceAgesExceedMax: OrderStatusReasonUnspecified,
    ReportAgeExceedsLastUpdate: OrderStatusReasonUnspecified,
    ReportPriceIsNegative: OrderStatusReasonUnspecified,
    TooManyBytesInName: OrderStatusReasonUnspecified,
    TooManyBytesInSymbol: OrderStatusReasonUnspecified,
    UnsupportedOperation: OrderStatusReasonUnspecified,
    UpdateOracleFailed: OrderStatusReasonUnspecified,
    WithdrawRateLimitExceeded: OrderStatusReasonUnspecified,

    // Order lock and permission errors
    InvalidOrderLock: OrderStatusReasonUnspecified,
    OrderLockDoesntExistSevere: OrderStatusReasonUnspecified,
    OrderLockExistsSevere: OrderStatusReasonUnspecified,
    PermissionlessCancelOrdersOutOfRange: OrderStatusReasonUnspecified,

    // Position and account errors
    PositionDoesNotExist: OrderStatusReasonUnspecified,
    PositionAlreadyInListSevere: OrderStatusReasonUnspecified,
    InsufficentAmountToOpenAccount: OrderStatusReasonUnspecified,
    IncompatibleCollateralToken: OrderStatusReasonUnspecified,

    // System integrity errors
    NullOrderIdSpecifiedSevere: OrderStatusReasonUnspecified,
    OrderDoesntExistSevere: OrderStatusReasonUnspecified,
    OrderIdIsNotAtSpecifiedPriceLevel: OrderStatusReasonUnspecified,
    PerpInsolvencyCheckFailedSevere: OrderStatusReasonUnspecified,
    OpenInterestInequalSevere: OrderStatusReasonUnspecified,
    CantClearNullOrderId: OrderStatusReasonUnspecified,
    CantClearSlotsOnExistingPerp: OrderStatusReasonUnspecified,
    CancelOrdersBeforeRemovingContract: OrderStatusReasonUnspecified,

    // Order lock clearing
    ClearingRemainingOrderLockBeyondBalance: OrderStatusReasonClearingRemainingOrderLockBeyondBalance,

    // Trigger execution errors
    PriceSetDuringTriggerExec: OrderStatusReasonPriceSetDuringTriggerExec,

    // Link and oracle errors
    InvalidLinkReportForContract: OrderStatusReasonUnspecified,
    InvalidLinkReportVersion: OrderStatusReasonUnspecified,
    LinkDsOracleNotConfigured: OrderStatusReasonUnspecified,
    LinkDsPriceUninitialized: OrderStatusReasonUnspecified,
  };

  return mapping[abiErrorName] ?? OrderStatusReasonUnspecified;
}

export function getOrderErrorInfo(
  errorReason: OrderStatusReason,
  context?: string,
  failureReason?: OrderFailureReason
): ErrorInfo {
  const detail = failureReason != null ? orderFailureMapping[failureReason] : undefined;

  // Always a fresh object — `context` below mutates it
  const errorInfo: ErrorInfo = detail
    ? {
        title: detail.title,
        message: [orderFailureStageLead[errorReason], detail.message].filter(Boolean).join(' '),
        severity: detail.severity,
      }
    : { ...(orderErrorMapping[errorReason] || orderErrorMapping[OrderStatusReasonUnspecified]) };

  if (context) {
    errorInfo.message = `${errorInfo.message} (${context})`;
  }

  return errorInfo;
}
